import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as fromActions from '@redux/modules/remote-services/dtech-remote/actions';
import { combineLatestWith, switchMap, tap, withLatestFrom, catchError } from 'rxjs/operators';
import { firstValueFrom, of } from 'rxjs';
import {
  selectCustomerVehicles,
  selectDTechRemoteAssistances,
  selectDTechRemoteAssistancesCompleted,
  selectDTechRemoteAssistancesCustomer,
} from '@redux/modules/remote-services/dtech-remote';
import { environment } from '@env';
import { Store } from '@ngrx/store';
import { DTechRemoteAssistanceCustomerService } from '@services/api/api.service';
import { DTechRemoteService, RemoteServiceKind } from '@services/api/sdf-remote-services-api.service';
import { AuthService } from '@auth0/auth0-angular';
import { loadedOrganizationsSubscriber, selectSelectedOrganizationId } from '@redux/index';

@Injectable()
export class DTechRemoteEffects {
  private hubConnection: HubConnection;

  setDTechRemoteAssistancesFilters$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setDTechRemoteAssistancesFilters),
    tap(() => {
      this.store.dispatch(fromActions.setDTechRemoteAssistancesPaginator({ paginator: { page: 1, pageSize: 10 } }));
    }),
  ), { dispatch: false });

  getDTechRemoteAssistancesList$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.getDTechRemoteAssistancesList,
      fromActions.setDTechRemoteAssistancesPaginator),
    withLatestFrom(
      this.store.select(selectDTechRemoteAssistances),
    ),
    tap(() => this.store.dispatch(fromActions.setDTechRemoteAssistancesLoading({ isLoading: true }))),
    switchMap(async ([_, vehicles]) => {
      const runningAndActivatedOnly = vehicles.filters.runningAndActivatedOnly === true;
      const listResponse = await firstValueFrom(this.dTechRemoteService.getDTechRemoteList(
        runningAndActivatedOnly || vehicles.filters.runningOnly ? true : undefined,
          vehicles.filters.connectedOnly ? vehicles.filters.connectedOnly : undefined,
          vehicles.filters.acceptedConsentOnly ? vehicles.filters.acceptedConsentOnly : undefined,
          runningAndActivatedOnly ? true : undefined,
          vehicles.paginator.page,
          vehicles.paginator.pageSize,
          vehicles.filters.qSearch ? vehicles.filters.qSearch : undefined));
      return [
        fromActions.setDTechRemoteAssistancesList({ response: listResponse }),
        fromActions.setDTechRemoteAssistancesLoading({ isLoading: false }),
      ];
    }),
    catchError(() => of([
      fromActions.setDTechRemoteAssistancesList({ response: { remoteServices: [], total: 0 } }),
      fromActions.setDTechRemoteAssistancesLoading({ isLoading: false }),
    ])),
    switchMap((actions) => [...actions]),
  ));

  setDTechRemoteAssistancesCompletedFilters$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setDTechRemoteAssistancesCompletedFilters),
    tap(() => {
      this.store.dispatch(fromActions.setDTechRemoteAssistancesCompletedPaginator({ paginator: { page: 1, pageSize: 10 } }));
    }),
  ), { dispatch: false });

  getDTechRemoteAssistancesCompletedList$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.getDTechRemoteAssistancesCompletedList,
      fromActions.setDTechRemoteAssistancesCompletedPaginator),
    withLatestFrom(
      this.store.select(selectDTechRemoteAssistancesCompleted),
    ),
    tap(() => this.store.dispatch(fromActions.setDTechRemoteAssistancesCompletedLoading({ isLoading: true }))),
    switchMap(async ([_, vehicles]) => {
      const listResponse = await firstValueFrom(this.dTechRemoteService.getDTechRemoteList(
        undefined,
        undefined,
        undefined,
        undefined,
          vehicles.paginator.page,
          vehicles.paginator.pageSize,
          vehicles.filters.qSearch));
      return [
        fromActions.setDTechRemoteAssistancesCompletedList({ response: listResponse }),
        fromActions.setDTechRemoteAssistancesCompletedLoading({ isLoading: false }),
      ];
    }),
    catchError(() => of([
      fromActions.setDTechRemoteAssistancesCompletedList({ response: { remoteServices: [], total: 0 } }),
      fromActions.setDTechRemoteAssistancesCompletedLoading({ isLoading: false }),
    ])),
    switchMap((actions) => [...actions]),
  ));

  public obtainWsDTechRemoteAssistanceStatus$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.obtainWsDTechRemoteAssistanceStatus),
    tap(async (action) => {
      if (this.hubConnection) {
        await this.hubConnection.stop().catch(() => undefined);
      }

      const accessToken = await firstValueFrom(this.auth.getAccessTokenSilently());
      const assistanceId = action.assistanceId;
      this.hubConnection = new HubConnectionBuilder()
        .withUrl(environment.remoteServicesWsURL, {
          accessTokenFactory: () => accessToken,
        })
        .withAutomaticReconnect()
        .build();
      this.hubConnection.on('ReceivedMessage', (connectionStatus, key) => {
        console.log('connectionStatus', connectionStatus);
        this.store.dispatch(fromActions.setDTechRemoteAssistanceStatus({ status: connectionStatus, key }));
      });
      this.hubConnection.on('CustomerConsentUpdated', (consent: CustomerConsentUpdatedMessage) => {
        if (consent?.assistanceId?.toLowerCase() !== assistanceId?.toLowerCase()) {
          return;
        }

        this.store.dispatch(fromActions.setDTechRemoteAssistanceConsent({
          consent: {
            id: consent.consentId,
            remoteServiceId: consent.assistanceId,
            vin: consent.vin,
            status: consent.status,
            expiresAt: consent.expiresAt ?? undefined,
            respondedAt: consent.respondedAt ?? undefined,
            respondedByUserId: consent.respondedByUserId,
          },
        }));
      });
      this.hubConnection.start()
        .then(() => this.hubConnection.invoke('JoinGroup', {
          vin: action.vin,
          assistanceId: action.assistanceId,
          remoteServiceKind: RemoteServiceKind.DTechRemote,
        }))
        .catch((err) => console.log('error while establishing signalr connection: ' + err));
    }),
  ), { dispatch: false });

  public destroyWsDTechRemoteAssistanceStatus$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.destroyWsDTechRemoteAssistanceStatus),
    tap(() => {
      if (this.hubConnection) {
        this.hubConnection.stop()
          .then(() => console.log('SignalR connection stopped'))
          .catch((err) => console.log('error while stopping signalr connection: ' + err));
      }
    }),
  ), { dispatch: false });

  getCustomerVehiclesList$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.getCustomerVehiclesList,
      fromActions.setCustomerVehiclesPaginator,
      fromActions.setCustomerVehiclesFilters),
    combineLatestWith(
      this.store.select(selectSelectedOrganizationId),
      loadedOrganizationsSubscriber(this.store),
    ),
    withLatestFrom(
      this.store.select(selectCustomerVehicles),
    ),
    tap(() => this.store.dispatch(fromActions.setCustomerVehiclesLoading({ isLoading: true }))),
    switchMap(async ([[_, selectedOrganizationId], vehicles]) => {
      const vehiclesData = await firstValueFrom(this.dTechRemoteAssistanceCustomerService
        .getVehiclesCustomerList(selectedOrganizationId,
          vehicles.paginator.page,
          vehicles.paginator.pageSize,
          vehicles.filters.qSearch));
      return [
        fromActions.setCustomerVehiclesList({ response: vehiclesData }),
        fromActions.setCustomerVehiclesLoading({ isLoading: false }),
      ];
    }),
    catchError(() => of([
      fromActions.setCustomerVehiclesList({ response: { vehicles: [], total: 0 } }),
      fromActions.setCustomerVehiclesLoading({ isLoading: false }),
    ])),
    switchMap((actions) => [...actions]),
  ));

  getDTechRemoteAssistancesCustomerList$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.getDTechRemoteAssistancesCustomerList,
      fromActions.setDTechRemoteAssistancesCustomerPaginator,
      fromActions.setDTechRemoteAssistancesCustomerFilters),
    combineLatestWith(
      this.store.select(selectSelectedOrganizationId),
      loadedOrganizationsSubscriber(this.store),
    ),
    withLatestFrom(
      this.store.select(selectDTechRemoteAssistancesCustomer),
    ),
    tap(() => this.store.dispatch(fromActions.setDTechRemoteAssistancesCustomerLoading({ isLoading: true }))),
    switchMap(async ([[_, selectedOrganizationId], vehicles]) => {
      const vehiclesData = await firstValueFrom(this.dTechRemoteAssistanceCustomerService
        .getDTechRemoteAssistanceCustomerList(selectedOrganizationId,
          vehicles.filters.isCompleted,
          vehicles.paginator.page,
          vehicles.paginator.pageSize,
          vehicles.filters.startDate,
          vehicles.filters.endDate,
          vehicles.filters.qSearch));
      return [
        fromActions.setDTechRemoteAssistancesCustomerList({ response: vehiclesData }),
        fromActions.setDTechRemoteAssistancesCustomerLoading({ isLoading: false }),
      ];
    }),
    catchError(() => of([
      fromActions.setDTechRemoteAssistancesCustomerList({ response: { remoteAssistances: [], total: 0 } }),
      fromActions.setDTechRemoteAssistancesCustomerLoading({ isLoading: false }),
    ])),
    switchMap((actions) => [...actions]),
  ));


  constructor(
    private actions$: Actions,
    private store: Store,
    private dTechRemoteService: DTechRemoteService,
    private dTechRemoteAssistanceCustomerService: DTechRemoteAssistanceCustomerService,
    private auth: AuthService,
  ) {
  }
}

interface CustomerConsentUpdatedMessage {
  consentId: string;
  assistanceId: string;
  vin: string;
  status: string;
  expiresAt?: string | null;
  respondedAt?: string | null;
  respondedByUserId?: string | null;
}
